// src/components/GenericPortalLogins.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from './api'; 
import '../styles/SimList.css';
import '../styles/Table.css'; 
import '../styles/form.css';
import '../styles/modal.css'; 

function GenericPortalLogins({ portalType, title }) {
    const [records, setRecords] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    
    // --- Edit Modes States ---
    const [isGlobalEditMode, setIsGlobalEditMode] = useState(false);
    const [editedPasswords, setEditedPasswords] = useState({});
    const [editedEmails, setEditedEmails] = useState({});
    const [editedSuppliers, setEditedSuppliers] = useState({});
    const [editedContacts, setEditedContacts] = useState({});      // E-News: single contact
    const [editedFirstNames, setEditedFirstNames] = useState({});  // Artwork
    const [editedLastNames, setEditedLastNames] = useState({});    // Artwork
    
    // --- Bulk Selection States ---
    const [selectedIds, setSelectedIds] = useState([]);

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10); 
    
    // Create Item Form State
    const [showAddForm, setShowAddForm] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [newEntry, setNewForm] = useState({
        first_name: '',
        last_name: '',
        company: '',
        email: '',
        username: '',
        password: '',
        supplier: '',
        contact: '',
        enews_login: ''
    });

    const fileInputRef = useRef(null);

    // Context Configuration
    const isEnews = portalType === 'enews';
    const loginHeaderLabel = isEnews ? 'E-News Log-In' : 'Artwork Log-In';

    // Fetch details
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`/portal-logins/${portalType}`);
            setRecords(res.data);
            setSelectedIds([]); 
        } catch (err) {
            console.error(`Error loading data for ${portalType}:`, err);
        } finally {
            setIsLoading(false);
        }
    }, [portalType]);

    useEffect(() => {
        fetchData();
        setIsGlobalEditMode(false);
        setEditedPasswords({});
        setEditedEmails({});
        setEditedSuppliers({});
        setEditedContacts({});
        setEditedFirstNames({});
        setEditedLastNames({});
        setCurrentPage(1);
    }, [fetchData]);

    // Handle CSV File Upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            setIsLoading(true);
            const res = await api.post(`/portal-logins/${portalType}/upload-csv`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert(res.data.message + ` (${res.data.stats.inserted} added, ${res.data.stats.updated} updated)`);
            fetchData();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed uploading CSV');
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
            setIsLoading(false);
        }
    };

    // Supplier/Company accessor — drives the alphabetical ordering
    const getSupplierName = (item) => (isEnews ? item.supplier : item.company) || '';

    // Filter elements
    const filteredRecords = records
        .filter(item => {
            if (isEnews) {
                const fullSearchString = `${item.supplier || ''} ${item.contact || ''} ${item.email || ''} ${item.enews_login || ''}`.toLowerCase();
                return fullSearchString.includes(searchQuery.toLowerCase());
            } else {
                const fullSearchString = `${item.first_name || ''} ${item.last_name || ''} ${item.company || ''} ${item.email || ''} ${item.username || ''}`.toLowerCase();
                return fullSearchString.includes(searchQuery.toLowerCase());
            }
        })
        // Alphabetical by Supplier (case-insensitive), blanks last
        .sort((a, b) => {
            const aSupplier = getSupplierName(a).trim();
            const bSupplier = getSupplierName(b).trim();

            // Blanks first
            if (!aSupplier && bSupplier) return -1;
            if (aSupplier && !bSupplier) return 1;

            const bySupplier = aSupplier.localeCompare(bSupplier, undefined, { sensitivity: 'base' });
            if (bySupplier !== 0) return bySupplier;

            // Tie-breaker: contact / name within the same supplier
            const aContact = `${(isEnews ? a.contact : `${a.first_name || ''} ${a.last_name || ''}`) || ''}`.trim();
            const bContact = `${(isEnews ? b.contact : `${b.first_name || ''} ${b.last_name || ''}`) || ''}`.trim();
            return aContact.localeCompare(bContact, undefined, { sensitivity: 'base' });
        });


    // Reset pagination to page 1 whenever search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // --- Pagination Logic ---
    const totalItems = filteredRecords.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);
    const autoResizeTextarea = (e) => {
        e.target.style.height = 'auto';
        e.target.style.height = `${e.target.scrollHeight}px`;
    };

    // --- Checkbox Row Selection Logic ---
    const handleRowSelect = (id) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(item => item !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handleSelectAllOnPage = () => {
        const currentPageIds = currentRecords.map(r => r.id);
        const allSelected = currentPageIds.every(id => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds(selectedIds.filter(id => !currentPageIds.includes(id)));
        } else {
            const uniqueSelections = Array.from(new Set([...selectedIds, ...currentPageIds]));
            setSelectedIds(uniqueSelections);
        }
    };

    // --- Bulk Delete Action ---
    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Are you absolutely sure you want to bulk delete the ${selectedIds.length} selected credentials?`)) return;

        try {
            setIsLoading(true);
            await Promise.all(selectedIds.map(id => api.delete(`/portal-logins/${portalType}/${id}`)));
            alert('Selected credentials deleted successfully!');
            fetchData();
        } catch (err) {
            console.error('Error during bulk deletion:', err);
            alert('An error occurred during bulk deletion.');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Handle Single Inline Save (Supplier, Contact, Email, Password) ---
    const handleInlineSave = async (id) => {
        const record = records.find(r => r.id === id);
        if (!record) return;

        const updatedEmail = editedEmails[id] !== undefined ? editedEmails[id] : record.email;
        const updatedPass  = editedPasswords[id] !== undefined ? editedPasswords[id] : record.password;

        if (!updatedEmail || !updatedEmail.trim()) {
            alert('Email address cannot be blank.');
            return;
        }

        // Supplier maps to `supplier` (E-News) or `company` (Artwork)
        const supplierKey = isEnews ? 'supplier' : 'company';
        const updatedSupplier = editedSuppliers[id] !== undefined
            ? editedSuppliers[id]
            : (record[supplierKey] || '');

        // Contact maps to `contact` (E-News) or first_name/last_name (Artwork)
        const contactFields = isEnews
            ? {
                contact: editedContacts[id] !== undefined
                    ? editedContacts[id]
                    : (record.contact || '')
              }
            : {
                first_name: editedFirstNames[id] !== undefined
                    ? editedFirstNames[id]
                    : (record.first_name || ''),
                last_name: editedLastNames[id] !== undefined
                    ? editedLastNames[id]
                    : (record.last_name || '')
              };

        try {
            await api.put(`/portal-logins/${portalType}/${id}`, {
                ...record,
                email: updatedEmail.trim(),
                password: updatedPass,
                [supplierKey]: updatedSupplier,
                ...contactFields
            });

            // Clear per-row edit state
            const drop = (obj) => { const next = { ...obj }; delete next[id]; return next; };
            setEditedEmails(drop(editedEmails));
            setEditedPasswords(drop(editedPasswords));
            setEditedSuppliers(drop(editedSuppliers));
            setEditedContacts(drop(editedContacts));
            setEditedFirstNames(drop(editedFirstNames));
            setEditedLastNames(drop(editedLastNames));

            fetchData();
        } catch (err) {
            alert(err.response?.data?.error || 'Update failed');
        }
    };

    // Handle record deletion
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this credentials entry?')) return;
        try {
            await api.delete(`/portal-logins/${portalType}/${id}`);
            fetchData();
        } catch (err) {
            alert('Delete action failed');
        }
    };

    // Handle Add New Form Submission
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        try {
            await api.post(`/portal-logins/${portalType}`, newEntry);
            setFormSuccess('New entry saved successfully!');
            setNewForm({
                first_name: '', last_name: '', company: '', email: '', username: '', password: '',
                supplier: '', contact: '', enews_login: ''
            });
            setTimeout(() => {
                setShowAddForm(false);
                setFormSuccess('');
                fetchData();
            }, 1000);
        } catch (err) {
            setFormError(err.response?.data?.error || 'Form saving failed');
        }
    };

    const isAllOnPageSelected = currentRecords.length > 0 && currentRecords.every(r => selectedIds.includes(r.id));

    return (
        <div className="card" style={{ marginTop: '10px' }}>
            {/* Header Actions Grid */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ margin: 0, color: 'var(--primary)' }}>{title} Database</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {selectedIds.length > 0 && (
                        <button 
                            onClick={handleBulkDelete} 
                            className="btn btn-cancel" 
                            style={{ backgroundColor: '#d9534f', color: 'white', marginRight: '10px', fontWeight: 'bold' }}
                        >
                            Bulk Remove ({selectedIds.length})
                        </button>
                    )}
                    <button onClick={() => setShowAddForm(true)} className="btn btn-primary">Add Single Entry</button>
                    <input 
                        type="file" 
                        accept=".csv" 
                        ref={fileInputRef} 
                        onChange={handleCsvUpload} 
                        style={{ display: 'none' }} 
                    />
                    <button onClick={() => fileInputRef.current.click()} className="btn refresh-btn" style={{ margin: 0 }}>
                        Upload CSV Logins
                    </button>
                </div>
            </div>

            {/* Filters Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center', flex: 1, minWidth: '300px' }}>
                    <input 
                        type="text" 
                        placeholder="Search by supplier, contact, email..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ margin: 0, maxWidth: '400px' }}
                    />

                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                        <input 
                            type="checkbox" 
                            checked={isGlobalEditMode} 
                            onChange={(e) => setIsGlobalEditMode(e.target.checked)} 
                        />
                        Inline Edit Mode (Email & Pass)
                    </label>
                </div>

                {/* Rows Per Page Selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '600', color: 'var(--text-secondary)' }}>Show:</span>
                    <select 
                        value={itemsPerPage} 
                        onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1); 
                        }}
                        style={{
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: '1px solid var(--border-color)',
                            background: 'white',
                            fontWeight: '600',
                            outline: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <option value={10}>10 rows</option>
                        <option value={15}>15 rows</option>
                        <option value={30}>30 rows</option>
                    </select>
                </div>
            </div>

            {/* TABLE VIEW */}
            {isLoading ? (
                <p>Loading logins dataset...</p>
            ) : (
                <>
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '40px', textAlign: 'center' }}>
                                        <input 
                                            type="checkbox" 
                                            checked={isAllOnPageSelected}
                                            onChange={handleSelectAllOnPage}
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </th>
                                    <th>Supplier</th>
                                    <th>Contact</th>
                                    <th>Email</th>
                                    {isEnews && <th>{loginHeaderLabel}</th>}
                                    <th style={{ width: '250px' }}>Password</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentRecords.length === 0 ? (
                                    <tr>
                                        <td colSpan={isEnews ? "7" : "6"} className="no-data-message">
                                            No credentials matching the filters were found.
                                        </td>
                                    </tr>
                                ) : (
                                    currentRecords.map(row => {
                                        const isSelected = selectedIds.includes(row.id);
                                        
                                        // Dynamic row mappings depending on actual DB keys
                                        const supplierVal = isEnews ? row.supplier : row.company;
                                        const contactVal = isEnews ? row.contact : `${row.first_name || ''} ${row.last_name || ''}`.trim();
                                        const loginVal = isEnews ? row.enews_login : row.username;

                                        return (
                                            <tr 
                                                key={row.id} 
                                                className={isSelected ? "selected-row" : ""}
                                            >
                                                <td style={{ textAlign: 'center' }}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={isSelected}
                                                        onChange={() => handleRowSelect(row.id)}
                                                        style={{ cursor: 'pointer' }}
                                                    />
                                                </td>

                                                {/* Supplier / Company */}
                                                <td>
                                                    {isGlobalEditMode ? (
                                                        <textarea
                                                            className="inline-edit-textarea"
                                                            value={editedSuppliers[row.id] !== undefined ? editedSuppliers[row.id] : (supplierVal || '')}
                                                            onChange={(e) => {
                                                                setEditedSuppliers({ ...editedSuppliers, [row.id]: e.target.value });
                                                                autoResizeTextarea(e);
                                                            }}
                                                            onFocus={autoResizeTextarea}
                                                            rows={1}
                                                        />
                                                    ) : (
                                                        supplierVal || <span className="italic-placeholder">-</span>
                                                    )}
                                                </td>

                                                {/* Contact — one field for E-News, first + last for Artwork */}
                                                <td>
                                                    {isGlobalEditMode ? (
                                                        isEnews ? (
                                                            <textarea
                                                                className="inline-edit-textarea"
                                                                value={editedContacts[row.id] !== undefined ? editedContacts[row.id] : (row.contact || '')}
                                                                onChange={(e) => {
                                                                    setEditedContacts({ ...editedContacts, [row.id]: e.target.value });
                                                                    autoResizeTextarea(e);
                                                                }}
                                                                onFocus={autoResizeTextarea}
                                                                rows={1}
                                                            />
                                                        ) : (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                <input
                                                                    type="text"
                                                                    placeholder="First name"
                                                                    value={editedFirstNames[row.id] !== undefined ? editedFirstNames[row.id] : (row.first_name || '')}
                                                                    onChange={(e) => setEditedFirstNames({ ...editedFirstNames, [row.id]: e.target.value })}
                                                                    style={{ margin: 0, padding: '4px 8px', fontSize: '14px' }}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    placeholder="Last name"
                                                                    value={editedLastNames[row.id] !== undefined ? editedLastNames[row.id] : (row.last_name || '')}
                                                                    onChange={(e) => setEditedLastNames({ ...editedLastNames, [row.id]: e.target.value })}
                                                                    style={{ margin: 0, padding: '4px 8px', fontSize: '14px' }}
                                                                />
                                                            </div>
                                                        )
                                                    ) : (
                                                        contactVal || <span className="italic-placeholder">-</span>
                                                    )}
                                                </td>

                                                {/* Email Column */}
                                                <td style={{ wordBreak: 'break-all', minWidth: '220px' }}>
                                                    {isGlobalEditMode ? (
                                                        <textarea
                                                            className="inline-edit-textarea"
                                                            value={editedEmails[row.id] !== undefined ? editedEmails[row.id] : row.email}
                                                            onChange={(e) => {
                                                                setEditedEmails({
                                                                    ...editedEmails,
                                                                    [row.id]: e.target.value
                                                                });
                                                                autoResizeTextarea(e);
                                                            }}
                                                            onFocus={autoResizeTextarea}
                                                            rows={1}
                                                        />
                                                    ) : (
                                                        <strong style={{ wordBreak: 'break-all' }}>{row.email}</strong>
                                                    )}
                                                </td>

                                                {/* Only show for E-News */}
                                                {isEnews && <td>{loginVal || <span className="italic-placeholder">-</span>}</td>}

                                                {/* Password Column */}
                                                <td>
                                                    {isGlobalEditMode ? (
                                                        <input 
                                                            type="text" 
                                                            value={editedPasswords[row.id] !== undefined ? editedPasswords[row.id] : (row.password || '')}
                                                            onChange={(e) => setEditedPasswords({
                                                                ...editedPasswords,
                                                                [row.id]: e.target.value
                                                            })}
                                                            style={{ margin: 0, padding: '4px 8px', fontSize: '14px' }}
                                                        />
                                                    ) : (
                                                        <span style={{ fontFamily: 'monospace', background: '#f1f3f5', padding: '4px 8px', borderRadius: '4px', border: '1px solid #e9ecef', fontWeight: '500' }}>
                                                            {row.password || <span className="italic-placeholder">Empty</span>}
                                                        </span>
                                                    )}
                                                </td>

                                                <td>
                                                    {isGlobalEditMode ? (
                                                        <button 
                                                            onClick={() => handleInlineSave(row.id)}
                                                            className="btn btn-primary"
                                                            style={{ padding: '4px 10px', fontSize: '12px', marginRight: '5px' }}
                                                        >
                                                            Save
                                                        </button>
                                                    ) : (
                                                        <button 
                                                            onClick={() => handleDelete(row.id)}
                                                            className="btn-cancel" 
                                                            style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 'bold' }}
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Unified Pagination Controller Layout */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '10px 0', borderTop: '1px solid var(--border-color)' }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                            Showing {totalItems === 0 ? 0 : indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
                        </div>
                        
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                disabled={currentPage === 1}
                                className="btn"
                                style={{
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    backgroundColor: currentPage === 1 ? '#e9ecef' : 'var(--primary)',
                                    color: currentPage === 1 ? '#adb5bd' : 'white',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    transition: '0.1s'
                                }}
                            >
                                Previous
                            </button>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '0 10px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                    Page {currentPage} of {totalPages}
                                </span>
                            </div>

                            <button 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                disabled={currentPage === totalPages}
                                className="btn"
                                style={{
                                    padding: '6px 12px',
                                    fontSize: '13px',
                                    backgroundColor: currentPage === totalPages ? '#e9ecef' : 'var(--primary)',
                                    color: currentPage === totalPages ? '#adb5bd' : 'white',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    transition: '0.1s'
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* ADD ENTRY MODAL */}
            {showAddForm && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        
                        <div className="modal-header">
                            <h2>Add Single Login Entry</h2>
                            <button 
                                type="button" 
                                className="modal-close-btn" 
                                onClick={() => setShowAddForm(false)}
                            >
                                &times;
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                            <div className="modal-body">
                                {formError && <div className="message error">{formError}</div>}
                                {formSuccess && <div className="message success">{formSuccess}</div>}

                                {isEnews ? (
                                    <>
                                        <label className="modal-label">Supplier</label>
                                        <input 
                                            type="text" 
                                            value={newEntry.supplier} 
                                            onChange={(e) => setNewForm({ ...newEntry, supplier: e.target.value })} 
                                        />

                                        <label className="modal-label">Contact</label>
                                        <input 
                                            type="text" 
                                            value={newEntry.contact} 
                                            onChange={(e) => setNewForm({ ...newEntry, contact: e.target.value })} 
                                        />

                                        <label className="modal-label">Email (Required)</label>
                                        <input 
                                            type="email" 
                                            required
                                            value={newEntry.email} 
                                            onChange={(e) => setNewForm({ ...newEntry, email: e.target.value })} 
                                        />

                                        <label className="modal-label">E-News Log-In</label>
                                        <input 
                                            type="text" 
                                            value={newEntry.enews_login} 
                                            onChange={(e) => setNewForm({ ...newEntry, enews_login: e.target.value })} 
                                        />
                                    </>
                                ) : (
                                    <>
                                        <label className="modal-label">First Name</label>
                                        <input 
                                            type="text" 
                                            value={newEntry.first_name} 
                                            onChange={(e) => setNewForm({ ...newEntry, first_name: e.target.value })} 
                                        />

                                        <label className="modal-label">Last Name</label>
                                        <input 
                                            type="text" 
                                            value={newEntry.last_name} 
                                            onChange={(e) => setNewForm({ ...newEntry, last_name: e.target.value })} 
                                        />

                                        <label className="modal-label">Company / Supplier</label>
                                        <input 
                                            type="text" 
                                            value={newEntry.company} 
                                            onChange={(e) => setNewForm({ ...newEntry, company: e.target.value })} 
                                        />

                                        <label className="modal-label">Email Address (Required)</label>
                                        <input 
                                            type="email" 
                                            required
                                            value={newEntry.email} 
                                            onChange={(e) => setNewForm({ ...newEntry, email: e.target.value })} 
                                        />

                                        <label className="modal-label">Username / Log-In</label>
                                        <input 
                                            type="text" 
                                            value={newEntry.username} 
                                            onChange={(e) => setNewForm({ ...newEntry, username: e.target.value })} 
                                        />
                                    </>
                                )}

                                <label className="modal-label">Password</label>
                                <input 
                                    type="text" 
                                    value={newEntry.password} 
                                    onChange={(e) => setNewForm({ ...newEntry, password: e.target.value })} 
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-cancel">Cancel</button>
                                <button type="submit" className="btn btn-primary">Save Login</button>
                            </div>
                        </form>

                    </div>
                </div>
            )}
        </div>
    );
}

export default GenericPortalLogins;
